import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/stores/auth-store';
import { authAPI } from '@/lib/api';

interface AuthContextValue {
  isLoading: boolean;
  refetchProfile: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { isAuthenticated, token, setAuth, logout, setLoading, isTokenExpired } = useAuth();

  // Query to verify token and get user profile
  const {
    data: profileData,
    isLoading: isProfileLoading,
    refetch: refetchProfile,
    error,
  } = useQuery({
    queryKey: ['auth', 'profile'],
    queryFn: authAPI.getProfile,
    enabled: !!token && !isTokenExpired(),
    retry: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Set loading state based on query state
  useEffect(() => {
    setLoading(isProfileLoading);
  }, [isProfileLoading, setLoading]);

  // Handle profile data updates
  useEffect(() => {
    if (profileData?.success && profileData.data) {
      const user = profileData.data as any;
      setAuth(user, token!);
    }
  }, [profileData, token, setAuth]);

  // Handle authentication errors
  useEffect(() => {
    if (error) {
      console.error('Authentication error:', error);
      logout();
    }
  }, [error, logout]);

  // Check token expiration periodically
  useEffect(() => {
    if (!isAuthenticated || !token) return;

    const checkTokenExpiration = () => {
      if (isTokenExpired()) {
        console.log('Token expired, logging out');
        logout();
      }
    };

    // Check immediately
    checkTokenExpiration();

    // Set up interval to check every minute
    const interval = setInterval(checkTokenExpiration, 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated, token, isTokenExpired, logout]);

  const value: AuthContextValue = {
    isLoading: isProfileLoading,
    refetchProfile: () => {
      refetchProfile();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}